import type { NewProjectResolution } from '@/model/buildDefaultMainProject'
import { normalizeProjectResourceCdnUrl } from '@/features/static-resource/resourceCdn'

export type LocalStaticResourceItem = {
  sourceKey: string
  mappingPath: string
  relativePath: string
  folderSegments: string[]
  fileName: string
  md5: string
  size: number
  url: string
}

export type LocalStaticTemplatePreviewItem = {
  assetPath: string
  md5: string
  size: number
  url: string
}

export type LocalStaticTemplateItem = {
  id: string
  label: string
  order: number
  sourceOuiName: string
  ouiqPath: string
  preview: LocalStaticTemplatePreviewItem
  gallery: LocalStaticTemplatePreviewItem[]
}

export type LocalStaticResourceMap = {
  resolution: {
    width: number
    height: number
    key: string
  }
  rtp: {
    graphic: LocalStaticResourceItem[]
    audio: LocalStaticResourceItem[]
  }
  templates: LocalStaticTemplateItem[]
}

const resourceMapModules = import.meta.glob('../../assets/*/resource-map.json', {
  eager: true,
  import: 'default',
}) as Record<string, LocalStaticResourceMap>

const templateUrlModules = import.meta.glob('../../assets/*/templates/*.ouiq', {
  eager: true,
  import: 'default',
  query: '?url',
}) as Record<string, string>

function normalizeResourceItem(item: LocalStaticResourceItem): LocalStaticResourceItem {
  return {
    ...item,
    url: normalizeProjectResourceCdnUrl(item.url),
  }
}

function normalizePreviewItem(item: LocalStaticTemplatePreviewItem): LocalStaticTemplatePreviewItem {
  return {
    ...item,
    url: normalizeProjectResourceCdnUrl(item.url),
  }
}

function normalizeResourceMap(map: LocalStaticResourceMap): LocalStaticResourceMap {
  return {
    ...map,
    rtp: {
      graphic: map.rtp.graphic.map(normalizeResourceItem),
      audio: map.rtp.audio.map(normalizeResourceItem),
    },
    templates: map.templates.map((template) => ({
      ...template,
      preview: normalizePreviewItem(template.preview),
      gallery: template.gallery.map(normalizePreviewItem),
    })),
  }
}

const RESOURCE_MAPS = Object.values(resourceMapModules).map(normalizeResourceMap)

export function resolutionToLocalStaticKey(resolution: NewProjectResolution): string {
  return `${resolution.width}_${resolution.height}`
}

export function listLocalStaticResourceMaps(): LocalStaticResourceMap[] {
  return [...RESOURCE_MAPS]
}

export function getLocalStaticResourceMap(resolution: NewProjectResolution): LocalStaticResourceMap | null {
  const key = resolutionToLocalStaticKey(resolution)
  return RESOURCE_MAPS.find((item) => item.resolution.key === key) ?? null
}

export function resolveLocalStaticTemplateUrl(resolutionKey: string, ouiqPath: string): string {
  const suffix = `/assets/${resolutionKey}/${ouiqPath.replace(/\\/g, '/')}`
  return Object.entries(templateUrlModules).find(([key]) => key.endsWith(suffix))?.[1] ?? ''
}
